import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';
import './SiteStats.scss';

// 통계 > 웹사이트 방문자: 공개 웹사이트(churchappmore.com)의 방문 기록(client.siteVisits) 집계.
// 관리자 콘솔·교회 콘솔·로그인 화면 방문은 기록하지 않는다(components/SiteVisitTracker.tsx).
type Totals = { pageViews: number; visitors: number; sessions: number };
type DailyRow = Totals & { date: string };
type StatsData = {
  from: string;
  to: string;
  today: string;
  summary: Totals;
  quick: { today: Totals; yesterday: Totals; last7: Totals; last30: Totals };
  daily: DailyRow[];
  topPages: { path: string; pageViews: number; visitors: number }[];
  referrers: { host: string; sessions: number }[];
  devices: { deviceType: string; visitors: number }[];
};
type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

type Metric = keyof Totals;
const METRICS: { key: Metric; label: string }[] = [
  { key: 'visitors', label: '방문자' },
  { key: 'sessions', label: '방문' },
  { key: 'pageViews', label: '페이지뷰' },
];

const PRESETS = [7, 30, 90];

const PAGE_NAMES: Record<string, string> = {
  '/': '메인',
  '/service/member': '서비스소개 · 성도 앱',
  '/service/admin': '서비스소개 · 앱관리 앱',
  '/service/roll': '서비스소개 · 교적부 앱',
  '/service/finance': '서비스소개 · 재정 앱',
  '/service/sundayschool': '서비스소개 · 주일학교 앱',
  '/sample': '샘플',
  '/price': '요금',
  '/inquiry': '앱제작신청',
  '/inquiry-complete': '신청 완료',
  '/developer-account-guide': '개발자 계정 안내',
  '/company': '회사소개',
  '/company/notice': '공지사항',
  '/company/faq': '자주 묻는 질문',
  '/payment': '결제',
  '/contract': '계약서',
};

const DEVICE_NAMES: Record<string, string> = { desktop: 'PC', mobile: '휴대폰', tablet: '태블릿' };

function addDays(ymd: string, days: number) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function fmt(n: number) {
  return n.toLocaleString('ko-KR');
}

function shortDate(ymd: string) {
  const [, m, d] = ymd.split('-').map(Number);
  return `${m}/${d}`;
}

function weekday(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  return ['일', '월', '화', '수', '목', '금', '토'][new Date(y, m - 1, d).getDay()];
}

// 눈금을 1·2·5 단위로 맞춘 축 최댓값
function niceMax(value: number) {
  if (value <= 4) return 4;
  const power = 10 ** Math.floor(Math.log10(value));
  const unit = [1, 2, 5, 10].find((step) => step * power * 4 >= value) ?? 10;
  return unit * power * 4;
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

function DailyChart({ rows, metric }: { rows: DailyRow[]; metric: Metric }) {
  const [hover, setHover] = useState<number | null>(null);
  const metricLabel = METRICS.find((m) => m.key === metric)?.label ?? '';
  const max = niceMax(Math.max(0, ...rows.map((row) => row[metric])));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(max * ratio));
  // 날짜 글자가 겹치지 않게 대략 8개만 표시한다.
  const labelEvery = Math.max(1, Math.ceil(rows.length / 8));
  const hovered = hover === null ? null : rows[hover];

  return (
    <div className="site-stats-chart">
      <div className="site-stats-chart__plot" onMouseLeave={() => setHover(null)}>
        <div className="site-stats-chart__grid" aria-hidden>
          {ticks.slice().reverse().map((tick) => (
            <div key={tick} className="site-stats-chart__gridline">
              <span>{fmt(tick)}</span>
            </div>
          ))}
        </div>
        <div className="site-stats-chart__bars" role="img" aria-label={`일별 ${metricLabel} 막대 그래프`}>
          {rows.map((row, index) => (
            <div
              key={row.date}
              className={`site-stats-chart__slot${hover === index ? ' is-hover' : ''}`}
              onMouseEnter={() => setHover(index)}
              onClick={() => setHover(index)}
            >
              <div
                className="site-stats-chart__bar"
                style={{ height: `${max ? (row[metric] / max) * 100 : 0}%` }}
              />
            </div>
          ))}
        </div>
        {hovered && hover !== null && (
          <div
            className="site-stats-chart__tooltip"
            style={{
              left: `${((hover + 0.5) / rows.length) * 100}%`,
              transform: `translateX(${hover < rows.length / 2 ? '-10%' : '-90%'})`,
            }}
          >
            <strong>
              {hovered.date} ({weekday(hovered.date)})
            </strong>
            {METRICS.map((m) => (
              <div key={m.key} className={m.key === metric ? 'is-current' : ''}>
                <span>{m.label}</span>
                <b>{fmt(hovered[m.key])}</b>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="site-stats-chart__xaxis" aria-hidden>
        {rows.map((row, index) => (
          <span key={row.date}>{index % labelEvery === 0 ? shortDate(row.date) : ''}</span>
        ))}
      </div>
    </div>
  );
}

function ShareBar({ value, max }: { value: number; max: number }) {
  return (
    <div className="site-stats-share">
      <div className="site-stats-share__fill" style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
    </div>
  );
}

export default function SiteStats() {
  const session = getAdminSession();
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [preset, setPreset] = useState<number | null>(30);
  const [metric, setMetric] = useState<Metric>('visitors');
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showTable, setShowTable] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<StatsData>>(`${MainURL}/admin-console/site-stats`, {
        params: { requesterId: session.id, ...(range ?? {}) },
      });
      if (res.data?.ok && res.data.data) setData(res.data.data);
      else setErrorMsg(res.data?.message || '방문자 통계를 불러오지 못했습니다.');
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '방문자 통계를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // session은 매 렌더 새 객체라 id만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, session?.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const choosePreset = (days: number) => {
    const today = data?.today;
    setPreset(days);
    setRange(today ? { from: addDays(today, -(days - 1)), to: today } : null);
  };

  const changeDate = (field: 'from' | 'to', value: string) => {
    if (!value || !data) return;
    setPreset(null);
    setRange({ from: data.from, to: data.to, [field]: value });
  };

  const maxPageViews = useMemo(() => Math.max(0, ...(data?.topPages.map((p) => p.pageViews) ?? [])), [data]);
  const maxReferrer = useMemo(() => Math.max(0, ...(data?.referrers.map((r) => r.sessions) ?? [])), [data]);
  const deviceTotal = useMemo(() => (data?.devices ?? []).reduce((sum, d) => sum + d.visitors, 0), [data]);

  const quickCards = data
    ? [
        { label: '오늘', totals: data.quick.today },
        { label: '어제', totals: data.quick.yesterday },
        { label: '최근 7일', totals: data.quick.last7 },
        { label: '최근 30일', totals: data.quick.last30 },
      ]
    : [];

  return (
    <div className="Main-cover admin-panel site-stats">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">웹사이트 방문자</h2>
          <p className="admin-panel-desc">
            churchappmore.com 공개 페이지 방문 통계입니다. 방문자는 브라우저 기준(같은 사람이 PC와 휴대폰으로 오면 2명),
            방문은 브라우저 탭 세션 기준입니다. 관리자 콘솔·교회 콘솔·로그인 화면과 검색엔진 로봇은 세지 않습니다.
          </p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchStats} disabled={loading}>
          새로고침
        </button>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}
      {!data && loading && <div className="admin-state">불러오는 중…</div>}

      {data && (
        <>
          <div className="admin-dashboard-grid">
            {quickCards.map((card) => (
              <div key={card.label} className="admin-stat-card">
                <div className="admin-stat-label">{card.label} 방문자</div>
                <div className="admin-stat-value">{fmt(card.totals.visitors)}</div>
                <div className="admin-stat-sub">
                  방문 {fmt(card.totals.sessions)} · 페이지뷰 {fmt(card.totals.pageViews)}
                </div>
              </div>
            ))}
          </div>

          <div className="site-stats-filters">
            <div className="site-stats-segment">
              {PRESETS.map((days) => (
                <button
                  key={days}
                  type="button"
                  className={preset === days ? 'is-active' : ''}
                  onClick={() => choosePreset(days)}
                >
                  최근 {days}일
                </button>
              ))}
            </div>
            <div className="site-stats-dates">
              <input type="date" value={data.from} max={data.to} onChange={(e) => changeDate('from', e.target.value)} />
              <span>~</span>
              <input
                type="date"
                value={data.to}
                min={data.from}
                max={data.today}
                onChange={(e) => changeDate('to', e.target.value)}
              />
            </div>
          </div>

          <section className="site-stats-card">
            <div className="site-stats-card__head">
              <div>
                <h3>일별 {METRICS.find((m) => m.key === metric)?.label}</h3>
                <p>
                  {data.from} ~ {data.to} · 방문자 {fmt(data.summary.visitors)} · 방문 {fmt(data.summary.sessions)} · 페이지뷰{' '}
                  {fmt(data.summary.pageViews)}
                </p>
              </div>
              <div className="site-stats-segment">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={metric === m.key ? 'is-active' : ''}
                    onClick={() => setMetric(m.key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <DailyChart rows={data.daily} metric={metric} />
            <button type="button" className="site-stats-link" onClick={() => setShowTable((v) => !v)}>
              {showTable ? '표 닫기' : '표로 보기'}
            </button>
            {showTable && (
              <div className="site-stats-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>방문자</th>
                      <th>방문</th>
                      <th>페이지뷰</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily.slice().reverse().map((row) => (
                      <tr key={row.date}>
                        <td>
                          {row.date} ({weekday(row.date)})
                        </td>
                        <td>{fmt(row.visitors)}</td>
                        <td>{fmt(row.sessions)}</td>
                        <td>{fmt(row.pageViews)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="site-stats-columns">
            <section className="site-stats-card">
              <div className="site-stats-card__head">
                <h3>많이 본 페이지</h3>
              </div>
              {data.topPages.length === 0 ? (
                <div className="admin-state">기록이 없습니다.</div>
              ) : (
                <ul className="site-stats-list">
                  {data.topPages.map((page) => (
                    <li key={page.path}>
                      <div className="site-stats-list__row">
                        <span className="site-stats-list__name">
                          {PAGE_NAMES[page.path] ?? page.path}
                          {PAGE_NAMES[page.path] && <small>{page.path}</small>}
                        </span>
                        <span className="site-stats-list__value">
                          {fmt(page.pageViews)}
                          <small>방문자 {fmt(page.visitors)}</small>
                        </span>
                      </div>
                      <ShareBar value={page.pageViews} max={maxPageViews} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="site-stats-stack">
              <section className="site-stats-card">
                <div className="site-stats-card__head">
                  <h3>기기</h3>
                </div>
                {data.devices.length === 0 ? (
                  <div className="admin-state">기록이 없습니다.</div>
                ) : (
                  <ul className="site-stats-list">
                    {data.devices.map((device) => (
                      <li key={device.deviceType}>
                        <div className="site-stats-list__row">
                          <span className="site-stats-list__name">{DEVICE_NAMES[device.deviceType] ?? device.deviceType}</span>
                          <span className="site-stats-list__value">
                            {deviceTotal ? Math.round((device.visitors / deviceTotal) * 100) : 0}%
                            <small>방문자 {fmt(device.visitors)}</small>
                          </span>
                        </div>
                        <ShareBar value={device.visitors} max={deviceTotal} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="site-stats-card">
                <div className="site-stats-card__head">
                  <div>
                    <h3>유입 경로</h3>
                    <p>다른 사이트의 링크를 눌러 들어온 방문. 주소창 직접 입력·즐겨찾기·일부 앱은 나오지 않습니다.</p>
                  </div>
                </div>
                {data.referrers.length === 0 ? (
                  <div className="admin-state">기록이 없습니다.</div>
                ) : (
                  <ul className="site-stats-list">
                    {data.referrers.map((ref) => (
                      <li key={ref.host}>
                        <div className="site-stats-list__row">
                          <span className="site-stats-list__name">{ref.host}</span>
                          <span className="site-stats-list__value">{fmt(ref.sessions)}</span>
                        </div>
                        <ShareBar value={ref.sessions} max={maxReferrer} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
